import type { DetailedStructuresIndex, DetailedStructure, IndexMeta, SimpleIndexEntry } from '../types/structures';

export async function fetchDetailedStructuresIndex(): Promise<DetailedStructuresIndex> {
  const res = await fetch('/detailed_structures_index.json');
  if (!res.ok) throw new Error('Failed to fetch detailed_structures_index.json');
  return res.json();
}

export async function fetchStructureBySlug(slug: string): Promise<DetailedStructure> {
  const res = await fetch(`/detailed_structures/${slug}`);
  if (!res.ok) throw new Error(`Failed to fetch /detailed_structures/${slug}`);
  return res.json();
}

/**
 * Convenience: get the first slug from the index (useful as a sensible default)
 */
export async function pickFirstSlugFromIndex(): Promise<string | null> {
  const idx = await fetchDetailedStructuresIndex();
  const topKeys = Object.keys(idx);
  if (topKeys.length === 0) return null;
  const group = idx[topKeys[0]];
  if (!Array.isArray(group) || group.length === 0) return null;
  const firstEntry = group[0];
  const firstFilename = Object.keys(firstEntry)[0];
  return firstFilename || null;
}

/**
 * Return index-level metadata (the entry in detailed_structures_index.json) for a given slug filename
 */
export async function fetchIndexMetaForSlug(slug: string): Promise<IndexMeta | null> {
  const idx = await fetchDetailedStructuresIndex();
  const topKeys = Object.keys(idx);
  for (const key of topKeys) {
    const group = idx[key];
    if (!Array.isArray(group)) continue;
    for (const entry of group) {
      if (entry[slug]) return entry[slug] as IndexMeta;
    }
  }
  return null;
}

export async function flattenIndexToList(): Promise<SimpleIndexEntry[]> {
  const idx = await fetchDetailedStructuresIndex();
  const out: SimpleIndexEntry[] = [];

  for (const key of Object.keys(idx)) {
    const group = idx[key];
    if (!Array.isArray(group)) continue;

    for (const entry of group) {
      const filename = Object.keys(entry)[0];
      const meta = entry[filename] as IndexMeta; // mantieni il tipo forte

      let name = meta?.accommodation_name || filename;

      // Try to load the actual structure data to get NOME STRUTTURA (6.1) if available
      try {
        const structureData = await fetchStructureBySlug(filename);
        if (Array.isArray(structureData?.modules) && structureData.modules.length > 0) {
          const firstModule = structureData.modules[0];
          if (firstModule?.content) {
            // Try both numbered key (6.1) and human-readable key (NOME STRUTTURA)
            const nomeStruttura = firstModule.content["6.1"] || firstModule.content["NOME STRUTTURA"];
            if (typeof nomeStruttura === 'string' && nomeStruttura.trim()) {
              name = nomeStruttura;
            }
          }
        }
      } catch (e) {
        // If loading individual structure fails, continue with meta name
        void e;
      }

      const addr = meta?.address;
      const locality =
        typeof addr === "string"
          ? addr
          : (addr?.city ?? addr?.province ?? "");

      out.push({
        slug: filename,
        name: name,
        type: meta?.type || "",
        locality,
      });
    }
  }
  return out;
}

